_collow.components.QuizCode = class QuizCode extends _collow.components.Base {
  root;
  elements;
  params;
  codeMirrorInstance;
  result;
  studentOutput;
  
  // Static property declarations for better performance with class-level caching
  static scriptModeMap = {
    html: ["htmlmixed", "xml", "css", "javascript"], // Dependencies for htmlmixed
    mysql: ["sql"],
    json: ["javascript"], // JSON uses the JavaScript mode
    typescript: ["javascript"], // TypeScript uses the JavaScript mode
    golang: ["go"],
    csharp: ["clike"],
    java: ["clike"],
    scss: ["css"],
  };

  static modeMap = {
    html: "htmlmixed",
    mysql: "text/x-mysql",
    json: { name: "javascript", json: true },
    typescript: "text/typescript",
    golang: "text/x-go",
    csharp: "text/x-csharp",
    java: "text/x-java",
    scss: "text/x-scss",
  };

  constructor(root, elements, params) {
    super();

    this.root = root;
    this.elements = elements;
    this.params = params;
    
    // Load mode scripts and then initialize CodeMirror
    this.loadModeScripts()
        .then(() => {
          this.loadCodeMirror();
        })
        .catch((error) => {
          console.error("Failed to load mode scripts:", error);
        });
        
    this.removeAnswersHtml();

    // Use single event binding for performance
    const buttonElements = [elements.buttonRun, elements.buttonSubmit];
    for (const el of buttonElements) {
      if (el) {
        this.listen(el, "click", this.clickRunSubmitHandler);
      }
    }

    this.listen(document.body, "_collow.quiz-finished", this.showResults);
  }

  /**
   * Dynamically loads mode scripts for the given language.
   * @returns {Promise} Resolves when all scripts are successfully loaded.
   */
  loadModeScripts() {
    const language = this.params.language;
    const modes = QuizCode.scriptModeMap[language] || [language];

    // Return a Promise that resolves when all scripts are loaded
    return Promise.all(
        modes.map((mode) =>
            new Promise((resolve, reject) => {
              // Check if script is already loaded to avoid duplication
              const existingScript = document.querySelector(`script[src*="codemirror"][src*="${mode}.min.js"]`);
              if (existingScript) {
                resolve();
                return;
              }
              
              const script = document.createElement("script");
              script.src = `https://cdnjs.cloudflare.com/ajax/libs/codemirror/5.65.7/mode/${mode}/${mode}.min.js`;
              script.onload = resolve;
              script.onerror = () => reject(`Failed to load mode script: ${script.src}`);
              document.head.appendChild(script);
            })
        )
    );
  }

  /**
   * Initializes CodeMirror with the specified configuration.
   */
  loadCodeMirror() {
    const modeMap = QuizCode.modeMap;

    // Language mapping for syntax highlighting
    const codeMirrorConfig = {
      lineNumbers: true,
      tabSize: 2,
      value: this.params.boilerplate.trimEnd(),
      mode: modeMap?.[this.params.language] || this.params.language,
      theme: "monokai",
      gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter", "CodeMirror-lint-markers"],
      autoRefresh: true,
      lineWrapping: true,
      lint: true, // Enable linting
    };

    this.codeMirrorInstance = CodeMirror(this.elements.code, codeMirrorConfig);
  }

  removeAnswersHtml = () => {
    this.root.removeAttribute("data-quiz-code:expected-output");
    this.root.removeAttribute("data-quiz-code:example-code");
  };

  clickRunSubmitHandler = async ({ target }) => {
    const currentValue = this.codeMirrorInstance.getValue();
    const formData = new FormData();
    formData.append("source_code", currentValue);
    formData.append("language", this.params.language);
    formData.append("expected_output", this.params.expectedOutput);
    
    const isSubmit = target === this.elements.buttonSubmit;
    if (isSubmit) {
      formData.append("source_code_example", this.params.exampleCode);
    }

    this.elements.resultBox.removeAttribute("hidden");
    
    try {
      const result = await fetch(this.params.path + "/code_runner/submissions", {
        method: "post",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: formData,
      });
      
      if (!result.ok) {
        throw new Error(`HTTP error! status: ${result.status}`);
      }
      
      const resJson = await result.json();
      
      if (target === this.elements.buttonRun) {
        const output = resJson.stderr || resJson.stdout || resJson.compile_output;
        this.elements.testRunAnswer.innerHTML = this.escapeHtml(output);
        this.elements.testRunAnswer.removeAttribute("hidden");
      } else if (isSubmit) {
        this.elements.testRunAnswer.setAttribute("hidden", "");
        this.sendSubmitResult(resJson);
      }
    } catch (error) {
      console.error("Error running code:", error);
      this.dispatch(this.root, "_collow-custom-error", error.message || "An error occurred");
    }
  };

  sendSubmitResult = (resJson) => {
    const statusId = resJson.status.id;
    if (statusId === 11) {
      this.dispatch(
          this.root,
          "_collow-custom-error",
          resJson.status.description
      );
      return;
    }
    
    this.elements.submitted.removeAttribute("hidden");
    this.studentOutput = resJson.stdout?.trim();
    this.result = statusId === 3;

    // Hide buttons in one operation
    const buttons = [this.elements.buttonRun, this.elements.buttonSubmit];
    for (const el of buttons) {
      if (el) {
        el.setAttribute("hidden", "");
        this.dispatch(this.root, "_collow.show-finish-button");
      }
    }
    
    if (this.codeMirrorInstance && this.codeMirrorInstance.options) {
      this.codeMirrorInstance.options.readOnly = true;
    }

    const points = this.result ? this.params.points : 0;
    this.dispatch(this.root, "_collow.quiz-points", {
      points,
      index: this.params.index,
    });
  };

  showResults = () => {
    this.dispatch(this.root, `_collow.hint-${this.params.index}`, this.result);
    this.dispatch(
        this.root,
        "_collow.quiz-finished",
        { correct: this.result },
        false
    );
    
    // Fix: Only apply setAttribute to Element nodes, not text nodes or other node types
    const resultBoxChildren = this.elements.resultBox.childNodes;
    for (let i = 0; i < resultBoxChildren.length; i++) {
      const el = resultBoxChildren[i];
      if (el.nodeType === Node.ELEMENT_NODE) {
        el.setAttribute("hidden", "");
      }
    }

    if (this.result) {
      this.elements.correct.removeAttribute("hidden");
      this.elements.resultBox.dataset.quizCodeResult = true;
    } else {
      if (this.elements.expectedOutput) {
        this.elements.expectedOutput.textContent = this.escapeHtml(
          this.params.expectedOutput
        );
      }
      if (this.elements.personalOutput) {
        this.elements.personalOutput.textContent = this.escapeHtml(
          this.studentOutput
        );
      }
      if (this.elements.expectedOutput && this.elements.expectedOutput.parentNode) {
        this.elements.expectedOutput.parentNode.removeAttribute("hidden");
      }
      this.elements.resultBox.dataset.quizCodeResult = false;
    }
    
    this.showExampleCode();
  };

  showExampleCode = () => {
    if (!this.elements.answerCode) return;
    
    this.elements.answerCode.removeAttribute("hidden");
    
    // Use template string with proper escaping for HTML attributes
    const codeEscaped = this.escapeHtml(this.params.exampleCode).replace(/"/g, '&quot;');
    const inputCode = `
    <div
      class="course-lesson__code-block"
      data-component="CodeBlock"
      data-code-block:language="${this.escapeHtml(this.params.language)}"
      data-code-block:code="${codeEscaped}"
    ></div>`;
    
    this.elements.answerCode.insertAdjacentHTML("beforeend", inputCode);
  };

  escapeHtml = (text) => {
    if (typeof text !== "string") return text;
    
    // More efficient implementation using a single replace with callback
    return text.replace(/[&<>]/g, (match) => {
      switch (match) {
        case '&': return '&amp;';
        case '<': return '&lt;';
        case '>': return '&gt;';
        default: return match;
      }
    });
  };
};